// ─────────────────────────────────────────────────────────────────
// Glass-i — Goods Receipt Note create / edit / view page (Stage 6P.3).
//
// Routes:
//   /erp/purchase/grns/new              → blank create — operator picks a PO
//   /erp/purchase/grns/:id              → edit / view (status-gated)
//   /erp/purchase/grns/from-po/:poId    → server-prefilled create from PO
//
// Brief Section N: hosts header form (GRN meta + receipt logistics),
// item-type-aware inline line grid (Section O), live summary panel,
// and a workflow toolbar (Save Draft → Mark Received → Inspect →
// Accept / Cancel). Backend remains source of truth for ALL receipt
// math (received_sqmt / received_sqft / received_total_length,
// rejected area math, PO pending balance, accept reconciliation).
// Frontend mirrors via grnHelpers for instant feedback but never wins.
//
// Three load paths:
//   • edit (id)        → goodsReceiptsApi.get(id) + normalizeGoodsReceipt
//   • from-po (poId)   → goodsReceiptsApi.getPoReceiptBalance(poId)
//                        + purchaseOrdersApi.get(poId) → in-memory draft via
//                        buildGrnFromPoLines (NOT auto-persisted; first
//                        Save Draft calls goodsReceiptsApi.createFromPo).
//   • new              → empty draft, PO picker required.
//
// Save round-trip:
//   validateGoodsReceipt → buildGoodsReceiptPayload →
//   goodsReceiptsApi.create / update / createFromPo →
//   normalizeGoodsReceipt (re-hydrates from server numbers).
//
// Style: mirrors PurchaseOrderFormPage.css density (slate-50 header,
// 22 px rows, sticky toolbar, borderless inline editors, ⋯ row-actions).
// CSS is scoped to the co-located .css file — no global edits.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  message
} from 'antd';
import {
  ArrowLeftOutlined,
  AuditOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  InboxOutlined,
  LockOutlined,
  MoreOutlined,
  SaveOutlined,
  SearchOutlined,
  ShopOutlined,
  StopOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { goodsReceiptsApi } from './grnApi';
import { purchaseOrdersApi } from './purchaseApi';
import {
  GRN_INSPECTION_STATUS_OPTIONS,
  ITEM_TYPE_OPTIONS,
  actorDisplayName,
  buildGoodsReceiptPayload,
  buildGrnFromPoLines,
  // calculateReceivedSqmt / calculateReceivedSqft /
  // calculateReceivedTotalLength are wired in via
  // normalizeGoodsReceiptLine which routes glass + length item types
  // through them on every patch — so live recompute is guaranteed.
  canAcceptGrn,
  canCancelGrn,
  canEditGrn,
  canInspectGrn,
  canReceiveGrn,
  normalizeGoodsReceipt,
  normalizeGoodsReceiptLine,
  statusColor,
  statusDotColor,
  statusLabel,
  summarizeGrnLines,
  validateGoodsReceipt
} from './grnHelpers';
import './GoodsReceiptFormPage.css';

// ─────────────────────────────────────────────────────────────────
// Static option lists. UOMs mirror the PO editor so the operator
// sees the same vocabulary on both ends of the receipt cycle.
// ─────────────────────────────────────────────────────────────────
const UOM_OPTIONS = Object.freeze([
  'PCS', 'NOS', 'KG', 'GRM', 'LTR', 'MTR', 'RFT', 'RMT',
  'SQMT', 'SQFT', 'BOX', 'SET', 'PAIR', 'BUNDLE', 'DOZ', 'ROLL', 'SHEET'
].map((v) => ({ value: v, label: v })));

const GLASS_TYPES  = new Set(['GLASS']);
const LENGTH_TYPES = new Set(['PROFILE', 'UPVC', 'ALUMINIUM']);

// ─────────────────────────────────────────────────────────────────
// Tiny date helpers — keep dayjs out of the helpers module so the
// pure ESM math file stays node:test-friendly.
// ─────────────────────────────────────────────────────────────────
function todayDayjs() {
  return dayjs().startOf('day');
}

function isoToDayjs(v) {
  if (v === null || v === undefined || v === '') return null;
  const d = dayjs(v);
  return d.isValid() ? d : null;
}

function dayjsToIso(v) {
  if (!v) return null;
  if (typeof v === 'string') return v;
  if (dayjs.isDayjs(v)) return v.format('YYYY-MM-DD');
  const d = dayjs(v);
  return d.isValid() ? d.format('YYYY-MM-DD') : null;
}

function isoDateTimeToDayjs(v) {
  if (!v) return null;
  const d = dayjs(v);
  return d.isValid() ? d : null;
}

// The /from-po/:poId route normally carries a PO UUID, but a legacy link
// or a hand-typed URL may carry a PO No instead. Everything downstream
// (GET /orders/:id, /po-balance/:poId, POST /from-po/:poId) requires the
// UUID, so resolve a non-UUID to its id via the PO list (q matches po_no
// ILIKE) and require an exact po_no match. Returns null when unresolved.
const PO_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
async function resolvePoId(raw) {
  const val = String(raw || '').trim();
  if (!val) return null;
  if (PO_UUID_RE.test(val)) return val;
  try {
    const res = await purchaseOrdersApi.list({ q: val, limit: 20 });
    const list = Array.isArray(res)
      ? res
      : (Array.isArray(res?.rows) ? res.rows
        : (Array.isArray(res?.data) ? res.data : []));
    const wanted = val.toUpperCase();
    const match = list.find(
      (r) => String(r.po_no || r.poNo || '').trim().toUpperCase() === wanted);
    return (match && (match.id || match.po_id)) || null;
  } catch {
    return null;
  }
}

function dayjsToIsoDateTime(v) {
  if (!v) return null;
  if (typeof v === 'string') return v;
  if (dayjs.isDayjs(v)) return v.toISOString();
  const d = dayjs(v);
  return d.isValid() ? d.toISOString() : null;
}

// Number formatters for read-only derived columns.
const _fmtDim = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 4
});
function dim(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '';
  if (v === 0) return '';
  return _fmtDim.format(v);
}

const _fmtQty = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 4
});
function qty(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '0';
  return _fmtQty.format(v);
}

function errMessage(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

// Stable client-row id minter for freshly-added blank lines.
let _localRowSeq = 0;
function nextLocalRowId() {
  _localRowSeq += 1;
  return `grn-local-${Date.now()}-${_localRowSeq}`;
}

function makeEmptyLine() {
  return normalizeGoodsReceiptLine({
    _clientRowId:     nextLocalRowId(),
    _dirty:           true,
    item_type:        'OTHER',
    item_name:        '',
    uom:              'PCS',
    received_qty:     0,
    accepted_qty:     0,
    rejected_qty:     0,
    rejection_reason: null,
    remarks:          ''
  });
}

// Header defaults for create mode.
function makeEmptyHeader() {
  return {
    id:                  null,
    grnNo:               null,
    grnDate:             todayDayjs(),
    receivedDate:        todayDayjs(),
    status:              'DRAFT',
    inspectionStatus:    'PENDING',

    poId:                null,
    poNumberSnapshot:    null,
    poDateSnapshot:      null,

    supplierId:          null,
    supplierName:        '',
    supplierGstin:       '',
    supplierContact:     '',
    supplierAddress:     '',

    deliveryLocation:    '',
    deliveryNoteRef:     '',
    invoiceRef:          '',
    transporterRef:      '',
    vehicleNo:           '',

    remarks:             '',
    inspectionRemarks:   '',

    receivedBy:          '',
    receivedAt:          null,
    inspectedBy:         '',
    inspectedAt:         null,
    acceptedBy:          null,
    acceptedAt:          null,
    cancelledBy:         null,
    cancelledAt:         null,
    cancellationReason:  null,

    lines: []
  };
}

// Map the editor header state → AntD Form values. Date / datetime
// fields stay as dayjs so the pickers work; everything else is plain.
function headerToFormValues(h) {
  return {
    grnDate:           h.grnDate || null,
    receivedDate:      h.receivedDate || null,
    poNumberSnapshot:  h.poNumberSnapshot || '',
    supplierName:      h.supplierName || '',
    supplierGstin:     h.supplierGstin || '',
    supplierContact:   h.supplierContact || '',
    supplierAddress:   h.supplierAddress || '',
    invoiceRef:        h.invoiceRef || '',
    invoiceDate:       h.invoiceDate || null,
    deliveryNoteRef:   h.deliveryNoteRef || '',
    vehicleNo:         h.vehicleNo || '',
    transporterRef:    h.transporterRef || '',
    deliveryLocation:  h.deliveryLocation || '',
    // Show the resolved person name in the editable inputs, never the
    // raw actor UUID the receive/inspect endpoints stamp by default.
    receivedBy:        actorDisplayName(h.receivedByName, h.receivedBy),
    receivedAt:        h.receivedAt || null,
    inspectedBy:       actorDisplayName(h.inspectedByName, h.inspectedBy),
    inspectedAt:       h.inspectedAt || null,
    remarks:           h.remarks || ''
  };
}

// ─────────────────────────────────────────────────────────────────
// PO picker — small debounced search (used only in /new mode).
// ─────────────────────────────────────────────────────────────────
function PurchaseOrderPicker({ value, onPick, disabled }) {
  const [open, setOpen]         = useState(false);
  const [keyword, setKeyword]   = useState('');
  const [loading, setLoading]   = useState(false);
  const [rows, setRows]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Backend list contract: keyword is `q`, paging is `limit` / `offset`, and
  // `status` accepts a comma list. Only PLACED / PARTIALLY_RECEIVED POs can
  // receive goods, so the picker is scoped to those. Server-side paging keeps
  // it responsive as the order book grows.
  const search = useCallback(async ({ q = '', pageArg = 1, sizeArg = 10 }) => {
    setLoading(true);
    try {
      const params = {
        status: 'PLACED,PARTIALLY_RECEIVED',
        limit:  sizeArg,
        offset: (pageArg - 1) * sizeArg
      };
      if (q) params.q = q;
      const out = await purchaseOrdersApi.list(params);
      const list = Array.isArray(out)
        ? out
        : (out && Array.isArray(out.rows) ? out.rows
          : (out && Array.isArray(out.data) ? out.data : []));
      setRows(list);
      setTotal(out && typeof out.total === 'number' ? out.total : list.length);
    } catch (e) {
      message.error(errMessage(e, 'Failed to load purchase orders.'));
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // A new keyword always restarts at the first page.
  useEffect(() => { setPage(1); }, [keyword]);

  // Debounced fetch whenever the picker is open and its query changes.
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(
      () => search({ q: keyword.trim(), pageArg: page, sizeArg: pageSize }),
      220
    );
    return () => clearTimeout(t);
  }, [open, keyword, page, pageSize, search]);

  const openPicker = () => {
    if (disabled) return;
    setKeyword('');
    setPage(1);
    setOpen(true);
  };

  const pick = useCallback((r) => {
    onPick({
      id:              r.id || r.po_id,
      poNo:            r.poNo || r.po_no,
      supplierId:      r.supplierId || r.supplier_id,
      supplierName:    r.supplierName || r.supplier_name,
      supplierGstin:   r.supplierGstin || r.supplier_gstin,
      supplierContact: r.supplierContact || r.supplier_contact,
      supplierAddress: r.supplierAddress || r.supplier_address
    });
    setOpen(false);
  }, [onPick]);

  return (
    <>
      <Input
        readOnly
        value={value || ''}
        disabled={disabled}
        placeholder="Click to pick a PO…"
        suffix={
          <Button
            type="text"
            size="small"
            icon={<SearchOutlined />}
            disabled={disabled}
            onClick={openPicker}
          />
        }
        onClick={openPicker}
      />
      <Modal
        open={open}
        title="Select purchase order"
        onCancel={() => setOpen(false)}
        footer={null}
        width={780}
        destroyOnClose
        className="grn-po-picker"
      >
        <p className="grn-po-picker__hint">
          Only placed orders still awaiting receipt are listed.
        </p>
        <Input
          allowClear
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search by PO number or supplier…"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          className="grn-po-picker__search"
        />
        <Table
          className="grn-po-picker__grid"
          size="small"
          loading={loading}
          rowKey={(r) => r.id || r.po_id}
          dataSource={rows}
          onRow={(r) => ({ onClick: () => pick(r) })}
          scroll={{ y: 320 }}
          pagination={{
            current: page,
            pageSize,
            total,
            size: 'small',
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (t, range) =>
              `${range[0]}–${range[1]} of ${t} order${t === 1 ? '' : 's'}`,
            onChange: (p, s) => { setPage(p); setPageSize(s); }
          }}
          locale={{
            emptyText: keyword.trim()
              ? 'No placed orders match your search.'
              : 'No purchase orders are awaiting receipt.'
          }}
          columns={[
            {
              title: 'PO No',
              dataIndex: 'poNo',
              width: 150,
              render: (v, r) => (
                <span className="grn-po-picker__no">{v || r.po_no || '—'}</span>
              )
            },
            {
              title: 'PO Date',
              dataIndex: 'poDate',
              width: 120,
              render: (v, r) => {
                const d = isoToDayjs(v || r.po_date);
                return d ? d.format('DD-MMM-YYYY') : '—';
              }
            },
            {
              title: 'Supplier',
              dataIndex: 'supplierName',
              ellipsis: true,
              render: (v, r) => v || r.supplier_name || '—'
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 140,
              render: (v) => (
                <Tag color={statusColor(v)}>{statusLabel(v) || v}</Tag>
              )
            },
            {
              title: '',
              dataIndex: '_pick',
              width: 72,
              align: 'right',
              render: (_v, r) => (
                <Button
                  size="small"
                  type="link"
                  onClick={(e) => { e.stopPropagation(); pick(r); }}
                >
                  Use
                </Button>
              )
            }
          ]}
        />
      </Modal>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main page component.
// ─────────────────────────────────────────────────────────────────
export default function GoodsReceiptFormPage() {
  const { id, poId } = useParams();
  const navigate     = useNavigate();
  const isFromPo     = Boolean(poId);
  const isEdit       = Boolean(id);
  const isCreate     = !isEdit;

  const [form]                = Form.useForm();
  const [header, setHeader]   = useState(() => makeEmptyHeader());
  const [lines,  setLines]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [acting,  setActing]  = useState(false);

  const [cancelOpen,   setCancelOpen]   = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [inspectOpen,  setInspectOpen]  = useState(false);
  const [inspectLines, setInspectLines] = useState([]);
  const [inspectNotes, setInspectNotes] = useState('');

  // Ref for async readback.
  const headerRef = useRef(header);
  headerRef.current = header;

  // ── Capability gates ──────────────────────────────────────────
  const isEditable = useMemo(() => {
    if (isCreate || isFromPo) return true;
    return canEditGrn(header);
  }, [isCreate, isFromPo, header]);

  const isLocked = !isEditable;

  // ── Loaders ────────────────────────────────────────────────────
  const hydrateFromApi = useCallback((raw) => {
    const norm = normalizeGoodsReceipt(raw);
    const hydrated = Object.assign({}, norm, {
      grnDate:      isoToDayjs(norm.grnDate),
      receivedDate: isoToDayjs(norm.receivedDate),
      receivedAt:   isoDateTimeToDayjs(norm.receivedAt),
      inspectedAt:  isoDateTimeToDayjs(norm.inspectedAt),
      acceptedAt:   isoDateTimeToDayjs(norm.acceptedAt),
      cancelledAt:  isoDateTimeToDayjs(norm.cancelledAt)
    });
    setHeader(hydrated);
    setLines(norm.lines || []);
    form.setFieldsValue(headerToFormValues(hydrated));
  }, [form]);

  const loadGrn = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    try {
      const raw = await goodsReceiptsApi.get(id);
      hydrateFromApi(raw);
    } catch (e) {
      message.error(errMessage(e, 'Failed to load goods receipt.'));
    } finally {
      setLoading(false);
    }
  }, [id, isEdit, hydrateFromApi]);

  const loadFromPo = useCallback(async () => {
    if (!isFromPo) return;
    setLoading(true);
    try {
      // Resolve a non-UUID route param (PO No) to its id before any call —
      // the backend rejects a non-UUID with "id must be a valid UUID".
      const effectivePoId = await resolvePoId(poId);
      if (!effectivePoId) {
        message.error(`No purchase order found for "${poId}".`);
        return;
      }
      // Pull the PO + per-line pending balance in parallel.
      const [po, balance] = await Promise.all([
        purchaseOrdersApi.get(effectivePoId),
        goodsReceiptsApi.getPoReceiptBalance(effectivePoId).catch(() => null)
      ]);
      if (!po) {
        message.error('Purchase order not found.');
        return;
      }
      // Build an in-memory draft — DO NOT persist until Save Draft.
      const poLines = Array.isArray(po.lines) ? po.lines : [];
      const seededLines = buildGrnFromPoLines(poLines, balance);
      const draftHeader = Object.assign({}, makeEmptyHeader(), {
        poId:             po.id || po.po_id || effectivePoId,
        poNumberSnapshot: po.poNo || po.po_no || null,
        poDateSnapshot:   po.poDate || po.po_date || null,
        supplierId:       po.supplierId || po.supplier_id || null,
        supplierName:     po.supplierName || po.supplier_name || '',
        supplierGstin:    po.supplierGstin || po.supplier_gstin || '',
        supplierContact:  po.supplierContact || po.supplier_contact || '',
        supplierAddress:  po.supplierAddress || po.supplier_address || '',
        deliveryLocation: po.deliveryLocation || po.delivery_location || ''
      });
      setHeader(draftHeader);
      setLines(seededLines);
      form.setFieldsValue(headerToFormValues(draftHeader));
    } catch (e) {
      message.error(errMessage(e, 'Failed to seed from purchase order.'));
    } finally {
      setLoading(false);
    }
  }, [isFromPo, poId, form]);

  useEffect(() => {
    if (isEdit) {
      loadGrn();
    } else if (isFromPo) {
      loadFromPo();
    } else {
      form.setFieldsValue(headerToFormValues(headerRef.current));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, poId]);

  // ── PO picker (new mode only) ─────────────────────────────────
  const handlePickPo = useCallback(async (picked) => {
    if (!picked || !picked.id) return;
    setLoading(true);
    try {
      const [po, balance] = await Promise.all([
        purchaseOrdersApi.get(picked.id),
        goodsReceiptsApi.getPoReceiptBalance(picked.id).catch(() => null)
      ]);
      const poLines = Array.isArray(po?.lines) ? po.lines : [];
      const seededLines = buildGrnFromPoLines(poLines, balance);
      const next = Object.assign({}, headerRef.current, {
        poId:             po?.id || picked.id,
        poNumberSnapshot: po?.poNo || po?.po_no || picked.poNo || null,
        poDateSnapshot:   po?.poDate || po?.po_date || null,
        supplierId:       po?.supplierId || po?.supplier_id || picked.supplierId,
        supplierName:     po?.supplierName || po?.supplier_name || picked.supplierName || '',
        supplierGstin:    po?.supplierGstin || po?.supplier_gstin || picked.supplierGstin || '',
        supplierContact:  po?.supplierContact || po?.supplier_contact || picked.supplierContact || '',
        supplierAddress:  po?.supplierAddress || po?.supplier_address || picked.supplierAddress || '',
        deliveryLocation: po?.deliveryLocation || po?.delivery_location || ''
      });
      setHeader(next);
      setLines(seededLines);
      form.setFieldsValue(headerToFormValues(next));
      if (!seededLines.length) {
        message.info(
          'This purchase order is fully received — there is no pending quantity left to receive.'
        );
      }
    } catch (e) {
      message.error(errMessage(e, 'Failed to load purchase order.'));
    } finally {
      setLoading(false);
    }
  }, [form]);

  // ── Line patcher — every edit reroutes through the normaliser
  //    so receipt math (sqmt/sqft/total_length) refreshes instantly.
  const patchLine = useCallback((clientRowId, partial) => {
    setLines((prev) => prev.map((ln) => {
      if (ln._clientRowId !== clientRowId) return ln;
      const merged = Object.assign({}, ln, partial, { _dirty: true });
      return Object.assign({}, normalizeGoodsReceiptLine(merged), {
        _clientRowId: ln._clientRowId,
        _dirty:       true
      });
    }));
  }, []);

  const addLine = useCallback(() => {
    setLines((prev) => [...prev, makeEmptyLine()]);
  }, []);

  const removeLine = useCallback((clientRowId) => {
    setLines((prev) => prev.filter((ln) => ln._clientRowId !== clientRowId));
  }, []);

  // ── Per-line validation snapshot — drives inline cell errors and
  //    the Save button's enabled state. Maps clientRowId → {field: msg}.
  const lineErrors = useMemo(() => {
    const out = {};
    for (const ln of lines) {
      if (!ln || !ln._clientRowId) continue;
      const errs = {};
      const received = Number(ln.received_qty) || 0;
      const accepted = Number(ln.accepted_qty) || 0;
      const rejected = Number(ln.rejected_qty) || 0;
      if (accepted + rejected - received > 1e-6) {
        errs.accepted_qty = 'Accepted + Rejected exceeds Received.';
        errs.rejected_qty = 'Accepted + Rejected exceeds Received.';
      }
      if (rejected > 0) {
        const reason = (ln.rejection_reason || ln.rejectionReason || '').trim();
        if (!reason) {
          errs.rejection_reason = 'Reason required for any rejection.';
        }
      }
      if (Object.keys(errs).length > 0) out[ln._clientRowId] = errs;
    }
    return out;
  }, [lines]);

  const hasLineErrors = Object.keys(lineErrors).length > 0;

  // ── Summary panel ──────────────────────────────────────────────
  const summary = useMemo(() => summarizeGrnLines(lines), [lines]);

  // Which optional column sets are visible (at least one line of that
  // family present — keeps the grid horizontally calm for mixed POs).
  const showGlassCols  = useMemo(
    () => lines.some((l) => GLASS_TYPES.has(l.item_type)),  [lines]);
  const showLengthCols = useMemo(
    () => lines.some((l) => LENGTH_TYPES.has(l.item_type)), [lines]);

  // ── Snapshot for payload builder ──────────────────────────────
  function snapshot() {
    const v = form.getFieldsValue();
    return Object.assign({}, header, v, {
      id:           header.id,
      status:       header.status,
      grnDate:      dayjsToIso(v.grnDate),
      receivedDate: dayjsToIso(v.receivedDate),
      receivedAt:   dayjsToIsoDateTime(v.receivedAt),
      inspectedAt:  dayjsToIsoDateTime(v.inspectedAt),
      poId:         header.poId,
      poNumberSnapshot: header.poNumberSnapshot,
      lines
    });
  }

  // ── Save Draft ────────────────────────────────────────────────
  async function saveDraft() {
    if (isLocked) {
      message.warning('This goods receipt is locked.');
      return;
    }
    if (hasLineErrors) {
      message.error('Fix the highlighted line errors before saving.');
      return;
    }
    try {
      await form.validateFields();
    } catch (_) {
      message.error('Please fix the highlighted header fields.');
      return;
    }
    const state = snapshot();
    const { ok, errors } = validateGoodsReceipt(state);
    if (!ok) {
      message.error(errors[0].message);
      return;
    }
    const payload = buildGoodsReceiptPayload(state);
    setSaving(true);
    try {
      let saved;
      if (isEdit) {
        saved = await goodsReceiptsApi.update(header.id, payload);
      } else if (isFromPo && !header.id) {
        // First-save of a from-PO draft: createFromPo so the backend
        // can reconcile against the PO pending balance atomically. Use the
        // resolved PO UUID (header.poId) — the route param may be a PO No.
        saved = await goodsReceiptsApi.createFromPo(header.poId || poId, payload);
      } else {
        saved = await goodsReceiptsApi.create(payload);
      }
      const norm = normalizeGoodsReceipt(saved);
      message.success(isEdit
        ? `Goods receipt ${norm.grnNo || ''} updated.`
        : `Goods receipt ${norm.grnNo || ''} saved as draft.`);
      if (!isEdit && norm.id) {
        navigate(`/erp/purchase/grns/${norm.id}`, { replace: true });
      } else {
        hydrateFromApi(saved);
      }
    } catch (e) {
      message.error(errMessage(e, 'Failed to save goods receipt.'));
    } finally {
      setSaving(false);
    }
  }

  // ── Mark Received (DRAFT → RECEIVED) ──────────────────────────
  async function markReceived() {
    if (!header.id) {
      message.warning('Save the draft before marking received.');
      return;
    }
    if (!canReceiveGrn(header)) {
      message.warning('Only DRAFT goods receipts can be marked Received.');
      return;
    }
    Modal.confirm({
      title: 'Mark this GRN as Received?',
      icon: <ExclamationCircleOutlined />,
      content:
        'This freezes the header + lines from further edits and unlocks '
        + 'inspection. No stock or accounting entries are posted at this step.',
      okText: 'Mark Received',
      cancelText: 'Back',
      onOk: async () => {
        setActing(true);
        try {
          const out = await goodsReceiptsApi.receive(header.id, {});
          const norm = normalizeGoodsReceipt(out);
          message.success(`Goods receipt ${norm.grnNo || ''} marked Received.`);
          await loadGrn();
        } catch (e) {
          message.error(errMessage(e, 'Failed to mark received.'));
        } finally {
          setActing(false);
        }
      }
    });
  }

  // ── Inspect modal — per-line accepted/rejected/reason updates ─
  function openInspectModal() {
    if (!canInspectGrn(header)) {
      message.warning('Inspect is only available on RECEIVED goods receipts.');
      return;
    }
    // Seed editable copies for the modal.
    setInspectLines(lines.map((ln) => ({
      _clientRowId:     ln._clientRowId,
      item_name:        ln.item_name,
      item_type:        ln.item_type,
      uom:              ln.uom,
      received_qty:     Number(ln.received_qty) || 0,
      accepted_qty:     Number(ln.accepted_qty) || 0,
      rejected_qty:     Number(ln.rejected_qty) || 0,
      rejection_reason: ln.rejection_reason || ln.rejectionReason || '',
      inspection_status: ln.inspection_status || ln.inspectionStatus || 'PENDING'
    })));
    setInspectNotes('');
    setInspectOpen(true);
  }

  function patchInspectLine(cid, partial) {
    setInspectLines((prev) => prev.map((ln) => {
      if (ln._clientRowId !== cid) return ln;
      return Object.assign({}, ln, partial);
    }));
  }

  async function submitInspect() {
    // Last-mile reconcile check before firing the request.
    for (const ln of inspectLines) {
      const r = Number(ln.received_qty) || 0;
      const a = Number(ln.accepted_qty) || 0;
      const j = Number(ln.rejected_qty) || 0;
      if (a + j - r > 1e-6) {
        message.error(`${ln.item_name || 'Line'}: Accepted + Rejected exceeds Received.`);
        return;
      }
      if (j > 0 && !String(ln.rejection_reason || '').trim()) {
        message.error(`${ln.item_name || 'Line'}: Rejection reason is required.`);
        return;
      }
    }
    setActing(true);
    try {
      const payload = {
        inspection_remarks: inspectNotes || null,
        lines: inspectLines.map((ln) => ({
          client_row_id:     ln._clientRowId,
          accepted_qty:      Number(ln.accepted_qty) || 0,
          rejected_qty:      Number(ln.rejected_qty) || 0,
          rejection_reason:  ln.rejection_reason || null,
          inspection_status: ln.inspection_status || null
        }))
      };
      const out = await goodsReceiptsApi.inspect(header.id, payload);
      message.success('Inspection saved.');
      setInspectOpen(false);
      hydrateFromApi(out);
    } catch (e) {
      message.error(errMessage(e, 'Failed to save inspection.'));
    } finally {
      setActing(false);
    }
  }

  // ── Accept (RECEIVED → ACCEPTED / PARTIALLY_ACCEPTED) ──────────
  async function acceptGrn() {
    if (!canAcceptGrn(header)) {
      message.warning('Only RECEIVED goods receipts can be accepted.');
      return;
    }
    const anyRejected = lines.some((l) => (Number(l.rejected_qty) || 0) > 0);
    Modal.confirm({
      title: anyRejected
        ? 'Accept this GRN (with partial rejections)?'
        : 'Accept this GRN?',
      icon: <ExclamationCircleOutlined />,
      content: anyRejected
        ? 'Some lines have rejections. The receipt will be marked '
          + 'PARTIALLY_ACCEPTED and the rejected qty will not flow downstream.'
        : 'All received qty will be marked accepted. The linked PO will '
          + 'transition to PARTIALLY_RECEIVED if balance remains, '
          + 'otherwise stay PLACED until fully receipted.',
      okText: 'Accept',
      cancelText: 'Back',
      onOk: async () => {
        setActing(true);
        try {
          const out = await goodsReceiptsApi.accept(header.id);
          const norm = normalizeGoodsReceipt(out);
          message.success(
            `Goods receipt ${norm.grnNo || ''} ${statusLabel(norm.status)}.`);
          await loadGrn();
        } catch (e) {
          message.error(errMessage(e, 'Failed to accept goods receipt.'));
        } finally {
          setActing(false);
        }
      }
    });
  }

  // ── Cancel modal (DRAFT / RECEIVED → CANCELLED) ────────────────
  function openCancelModal() {
    if (!canCancelGrn(header)) {
      message.warning('Only DRAFT or RECEIVED goods receipts can be cancelled.');
      return;
    }
    setCancelReason('');
    setCancelOpen(true);
  }

  async function confirmCancel() {
    setActing(true);
    try {
      const out = await goodsReceiptsApi.cancel(header.id,
        { reason: cancelReason || undefined });
      const norm = normalizeGoodsReceipt(out);
      message.success(`Goods receipt ${norm.grnNo || ''} cancelled.`);
      setCancelOpen(false);
      await loadGrn();
    } catch (e) {
      message.error(errMessage(e, 'Failed to cancel goods receipt.'));
    } finally {
      setActing(false);
    }
  }

  // ── Line card renderer ────────────────────────────────────────
  // Each receipt line is a self-contained card instead of a row in a
  // ~18-column spreadsheet. It reads as a comparison — Ordered (the
  // read-only PO reference) vs Received (what you enter) vs Quality
  // (accept / reject) — and shows only the fields relevant to the
  // line's item type. No horizontal scrolling; every inline editor,
  // calculation and per-field validation behaves exactly as before.
  const renderLineCard = useCallback((row, idx) => {
    const cid       = row._clientRowId;
    const poLinked  = Boolean(row.po_line_id || row.poLineId);
    const isGlass   = GLASS_TYPES.has(row.item_type);
    const isLength  = LENGTH_TYPES.has(row.item_type);
    const errs      = lineErrors[cid] || {};
    const fieldErr  = (f) => errs[f] || null;
    const rejected  = Number(row.rejected_qty) || 0;
    const hasErr    = Object.keys(errs).length > 0;
    const uomLabel  = row.uom || '';
    const poQtyVal  = row.po_qty ?? row.poQty;

    const ew = (node, msg) =>
      msg
        ? <Tooltip title={msg}><span className="grn-lc__ew">{node}</span></Tooltip>
        : node;

    return (
      <div
        key={cid}
        className={`grn-lc${row._dirty ? ' is-dirty' : ''}${hasErr ? ' is-error' : ''}`}
      >
        {/* identity */}
        <div className="grn-lc__head">
          <span className="grn-lc__idx">{idx + 1}</span>
          <Select
            size="small" variant="borderless" value={row.item_type || undefined}
            disabled={isLocked} className="grn-lc__type" options={ITEM_TYPE_OPTIONS}
            placeholder="Type"
            onChange={(val) => patchLine(cid, { item_type: val })}
          />
          <Input
            size="small" variant="borderless" value={row.item_name || ''}
            disabled={isLocked || poLinked} maxLength={500}
            className="grn-lc__name" placeholder="Item description"
            onChange={(e) => patchLine(cid, { item_name: e.target.value })}
          />
          <span className="grn-lc__uom">
            <label>UOM</label>
            <Select
              size="small" variant="borderless" value={row.uom || undefined}
              disabled={isLocked} showSearch options={UOM_OPTIONS}
              className="grn-lc__uom-sel" placeholder="UOM"
              filterOption={(input, opt) =>
                String(opt?.value || '').toLowerCase().includes(input.toLowerCase())}
              onChange={(val) => patchLine(cid, { uom: val })}
            />
          </span>
          <span className="grn-lc__act">
            {!isLocked && (poLinked ? (
              <Tooltip title="PO-linked lines can't be removed — zero the received qty instead.">
                <Button size="small" type="text" disabled icon={<LockOutlined />} />
              </Tooltip>
            ) : (
              <Dropdown
                trigger={['click']}
                menu={{
                  items: [{ key: 'delete', label: 'Delete line', icon: <DeleteOutlined />, danger: true }],
                  onClick: ({ key }) => { if (key === 'delete') removeLine(cid); }
                }}
              >
                <Button size="small" type="text" icon={<MoreOutlined />} />
              </Dropdown>
            ))}
          </span>
        </div>

        {/* ordered | received | quality */}
        <div className="grn-lc__body">

          {/* Ordered — read-only PO reference */}
          <section className="grn-lc__sec grn-lc__sec--ord">
            <div className="grn-lc__sec-t">Ordered</div>
            <div className="grn-lc__kv">
              <span>Qty</span>
              <b>{poQtyVal != null ? `${qty(poQtyVal)}${uomLabel ? ' ' + uomLabel : ''}` : '—'}</b>
            </div>
            {isGlass && (
              <>
                <div className="grn-lc__kv">
                  <span>Size</span>
                  <b>{dim(row.po_width_mm ?? row.poWidthMm)} × {dim(row.po_height_mm ?? row.poHeightMm)} mm</b>
                </div>
                <div className="grn-lc__kv">
                  <span>Area</span>
                  <b>{dim(row.po_sqmt ?? row.poSqmt)} m² · {dim(row.po_sqft ?? row.poSqft)} ft²</b>
                </div>
              </>
            )}
            {isLength && (
              <>
                <div className="grn-lc__kv">
                  <span>Length</span><b>{dim(row.po_length_value ?? row.poLengthValue)}</b>
                </div>
                <div className="grn-lc__kv">
                  <span>Total</span><b>{dim(row.po_total_length ?? row.poTotalLength)}</b>
                </div>
              </>
            )}
          </section>

          {/* Received — editable */}
          <section className="grn-lc__sec grn-lc__sec--rcv">
            <div className="grn-lc__sec-t">Received</div>
            <div className="grn-lc__field">
              <label>Qty</label>
              <InputNumber
                size="small" variant="borderless" controls={false} min={0} step={1}
                value={row.received_qty ?? null} disabled={isLocked} className="grn-lc__num"
                onChange={(n) => patchLine(cid, { received_qty: n == null ? 0 : Number(n) })}
              />
            </div>
            {isGlass && (
              <>
                <div className="grn-lc__field grn-lc__field--size">
                  <label>Size mm</label>
                  <span className="grn-lc__sizepair">
                    <InputNumber
                      size="small" variant="borderless" controls={false} min={0} step={1}
                      value={row.received_width_mm ?? null} disabled={isLocked} className="grn-lc__num"
                      onChange={(n) => patchLine(cid, { received_width_mm: n == null ? 0 : Number(n) })}
                    />
                    <span className="grn-lc__x">×</span>
                    <InputNumber
                      size="small" variant="borderless" controls={false} min={0} step={1}
                      value={row.received_height_mm ?? null} disabled={isLocked} className="grn-lc__num"
                      onChange={(n) => patchLine(cid, { received_height_mm: n == null ? 0 : Number(n) })}
                    />
                  </span>
                </div>
                <div className="grn-lc__kv grn-lc__kv--derived">
                  <span>Area</span>
                  <b>{dim(row.received_sqmt)} m² · {dim(row.received_sqft)} ft²</b>
                </div>
              </>
            )}
            {isLength && (
              <>
                <div className="grn-lc__field">
                  <label>Length</label>
                  <InputNumber
                    size="small" variant="borderless" controls={false} min={0} step={0.01}
                    value={row.received_length_value ?? null} disabled={isLocked} className="grn-lc__num"
                    onChange={(n) => patchLine(cid, { received_length_value: n == null ? 0 : Number(n) })}
                  />
                </div>
                <div className="grn-lc__kv grn-lc__kv--derived">
                  <span>Total</span><b>{dim(row.received_total_length)}</b>
                </div>
              </>
            )}
          </section>

          {/* Quality — accept / reject */}
          <section className="grn-lc__sec grn-lc__sec--qc">
            <div className="grn-lc__sec-t">Quality</div>
            <div className="grn-lc__field">
              <label>Accepted</label>
              {ew(
                <InputNumber
                  size="small" variant="borderless" controls={false} min={0} step={1}
                  status={fieldErr('accepted_qty') ? 'error' : undefined}
                  value={row.accepted_qty ?? null} disabled={isLocked} className="grn-lc__num"
                  onChange={(n) => patchLine(cid, { accepted_qty: n == null ? 0 : Number(n) })}
                />,
                fieldErr('accepted_qty')
              )}
            </div>
            <div className="grn-lc__field">
              <label>Rejected</label>
              {ew(
                <InputNumber
                  size="small" variant="borderless" controls={false} min={0} step={1}
                  status={fieldErr('rejected_qty') ? 'error' : undefined}
                  value={row.rejected_qty ?? null} disabled={isLocked} className="grn-lc__num"
                  onChange={(n) => patchLine(cid, { rejected_qty: n == null ? 0 : Number(n) })}
                />,
                fieldErr('rejected_qty')
              )}
            </div>
            <div className="grn-lc__field grn-lc__field--wide">
              <label>Reason</label>
              {ew(
                <Input
                  size="small" variant="borderless" value={row.rejection_reason || ''} maxLength={500}
                  disabled={isLocked} status={fieldErr('rejection_reason') ? 'error' : undefined}
                  placeholder={rejected > 0 ? 'Required' : 'Optional'}
                  onChange={(e) => patchLine(cid, { rejection_reason: e.target.value })}
                />,
                fieldErr('rejection_reason')
              )}
            </div>
          </section>
        </div>

        {/* remarks */}
        <div className="grn-lc__foot">
          <label>Remarks</label>
          <Input
            size="small" variant="borderless" value={row.remarks || ''} disabled={isLocked}
            maxLength={500} placeholder="Notes for this line"
            onChange={(e) => patchLine(cid, { remarks: e.target.value })}
          />
        </div>
      </div>
    );
  }, [isLocked, patchLine, removeLine, lineErrors]);

  // ── Action gates for the toolbar ───────────────────────────────
  const showReceive = isEdit && canReceiveGrn(header);
  const showInspect = isEdit && canInspectGrn(header);
  const showAccept  = isEdit && canAcceptGrn(header);
  const showCancel  = isEdit && canCancelGrn(header);

  const statusTag = (
    <span className="grn-status grn-status--head">
      <span className="grn-sdot" style={{ background: statusDotColor(header.status || 'DRAFT') }} />
      <span className="grn-sname">{statusLabel(header.status || 'DRAFT')}</span>
    </span>
  );

  const dirtyCount = useMemo(
    () => lines.filter((l) => l._dirty).length, [lines]);

  return (
    <div className="grn-shell">
      {/* ── Header strip ─────────────────────────────────────── */}
      <div className="grn-headstrip">
        <div className="grn-headstrip__lead">
          <span className="grn-headstrip__icon"><InboxOutlined /></span>
          <div className="grn-headstrip__text">
            <span className="grn-headstrip__title">
              {isEdit
                ? (header.grnNo
                    ? `Goods Receipt — ${header.grnNo}`
                    : 'Goods Receipt')
                : (isFromPo
                    ? 'New Goods Receipt (from PO)'
                    : 'New Goods Receipt')}
            </span>
            <span className="grn-headstrip__subtitle">
              {isLocked
                ? 'Read-only — this GRN is no longer in DRAFT.'
                : 'Edit header + line cells — derived totals refresh live.'}
            </span>
          </div>
        </div>
        <Space size={6}>
          {statusTag}
          {!isLocked && dirtyCount > 0 && (
            <span className="grn-stat-chip grn-stat-chip--dirty">
              <strong>{dirtyCount}</strong> unsaved
            </span>
          )}
          {isLocked && (
            <span className="grn-stat-chip">
              <LockOutlined /> locked
            </span>
          )}
        </Space>
      </div>

      {/* ── Sticky toolbar ───────────────────────────────────── */}
      <div className="grn-toolbar">
        <Button size="small" icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/erp/purchase/grns')}>
          Back to list
        </Button>
        <div className="grn-toolbar__spacer" />
        {showReceive && (
          <Button size="small" type="primary" icon={<CheckCircleOutlined />}
            onClick={markReceived} loading={acting}>
            Mark Received
          </Button>
        )}
        {showInspect && (
          <Button size="small" icon={<AuditOutlined />}
            onClick={openInspectModal} disabled={acting}>
            Inspect
          </Button>
        )}
        {showAccept && (
          <Button size="small" type="primary" icon={<CheckCircleOutlined />}
            onClick={acceptGrn} loading={acting}>
            Accept
          </Button>
        )}
        {showCancel && (
          <Button size="small" danger icon={<StopOutlined />}
            onClick={openCancelModal} disabled={acting}>
            Cancel GRN
          </Button>
        )}
      </div>

      <Spin spinning={loading}>
        {isLocked && (
          <Alert
            type="info"
            showIcon
            message={`Goods receipt is ${statusLabel(header.status)} — fields are read-only.`}
            description={
              header.status === 'CANCELLED' && header.cancellationReason
                ? `Cancellation reason: ${header.cancellationReason}`
                : null
            }
          />
        )}

        {/* ── Document steps on the receiving spine ──────────── */}
        <div className="grn-steps">

        {/* 1 — Receipt details */}
        <div className="grn-step grn-step--first">
          <div className="grn-step__rail" aria-hidden>
            <span className="grn-step__node">1</span>
          </div>
          <div className="grn-step__body">
        <Form
          form={form}
          layout="vertical"
          disabled={isLocked}
          initialValues={headerToFormValues(header)}
        >
          <div className="grn-card">
            <div className="grn-card__title">
              <span>Receipt details</span>
              <span className="grn-stat-chip">
                {header.grnNo || (isEdit ? '—' : 'Auto-generated on save')}
              </span>
            </div>
            <div className="grn-card__bd">
            <div className="grn-merge">

              {/* Receipt — the GRN's own identity + linked PO */}
              <div className="grn-merge__col">
                <div className="grn-merge__sub">
                  <span className="grn-merge__ico"><FileTextOutlined /></span>Receipt
                </div>
                <Form.Item label="GRN no">
                  <Input value={header.grnNo || ''} disabled
                    placeholder={isEdit ? '—' : 'Auto on save'} />
                </Form.Item>
                <Form.Item label="Date" name="grnDate"
                  rules={[{ required: true, message: 'GRN date is required.' }]}>
                  <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
                </Form.Item>
                <Form.Item label="PO">
                  <PurchaseOrderPicker
                    value={header.poNumberSnapshot || ''}
                    disabled={isLocked || isEdit || isFromPo}
                    onPick={handlePickPo}
                  />
                </Form.Item>
                <Form.Item label="PO no" name="poNumberSnapshot">
                  <Input value={header.poNumberSnapshot || ''} disabled placeholder="—" />
                </Form.Item>
              </div>

              {/* Supplier — pulled from the picked PO */}
              <div className="grn-merge__col">
                <div className="grn-merge__sub">
                  <span className="grn-merge__ico"><ShopOutlined /></span>Supplier
                </div>
                <Form.Item label="Name" name="supplierName"
                  rules={[{ required: true, message: 'Supplier name is required.' }]}>
                  <Input maxLength={200} disabled placeholder="From purchase order" />
                </Form.Item>
                <Form.Item label="GSTIN" name="supplierGstin">
                  <Input maxLength={15} disabled placeholder="—" />
                </Form.Item>
                <Form.Item label="Inv no" name="invoiceRef">
                  <Input maxLength={200} placeholder="Vendor invoice no" />
                </Form.Item>
                <Form.Item label="Inv date" name="invoiceDate">
                  <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
                </Form.Item>
              </div>

              {/* Delivery — how the goods arrived */}
              <div className="grn-merge__col">
                <div className="grn-merge__sub">
                  <span className="grn-merge__ico"><EnvironmentOutlined /></span>Delivery
                </div>
                <Form.Item label="Challan" name="deliveryNoteRef">
                  <Input maxLength={200} placeholder="Challan no" />
                </Form.Item>
                <Form.Item label="Vehicle" name="vehicleNo">
                  <Input maxLength={50} placeholder="MH-01-XX-1234" />
                </Form.Item>
                <Form.Item label="Transporter" name="transporterRef">
                  <Input maxLength={200} placeholder="Carrier" />
                </Form.Item>
                <Form.Item label="Warehouse" name="deliveryLocation">
                  <Input maxLength={500} placeholder="Site / warehouse" />
                </Form.Item>
              </div>

              {/* Handling — who received & inspected, and when */}
              <div className="grn-merge__col">
                <div className="grn-merge__sub">
                  <span className="grn-merge__ico"><AuditOutlined /></span>Handling
                </div>
                <Form.Item label="Recv by" name="receivedBy">
                  <Input maxLength={200} placeholder="Person taking delivery" />
                </Form.Item>
                <Form.Item label="Recv at" name="receivedAt">
                  <DatePicker showTime style={{ width: '100%' }} format="DD-MMM-YYYY HH:mm" />
                </Form.Item>
                <Form.Item label="Insp by" name="inspectedBy">
                  <Input maxLength={200} placeholder="QC officer" />
                </Form.Item>
                <Form.Item label="Insp at" name="inspectedAt">
                  <DatePicker showTime style={{ width: '100%' }} format="DD-MMM-YYYY HH:mm" />
                </Form.Item>
              </div>

              {/* Notes — full width */}
              <div className="grn-merge__full">
                <Form.Item label="Remarks" name="remarks">
                  <Input.TextArea rows={2} maxLength={2000} placeholder="Header notes" />
                </Form.Item>
              </div>

            </div>
            </div>
          </div>
        </Form>
          </div>
        </div>

        {/* 2 — Items received */}
        <div className="grn-step">
          <div className="grn-step__rail" aria-hidden>
            <span className="grn-step__node">2</span>
          </div>
          <div className="grn-step__body">
        {/* Body: lines grid + reconciliation rail */}
        <div className="grn-body">
          <div className="grn-lines-card">
            <div className="grn-lines-card__head">
              <span className="grn-lines-title">
                Lines ({lines.length})
              </span>
              {!isLocked && !isEdit && !isFromPo && (
                <Tooltip title="Pick a Purchase Order above to seed the line grid.">
                  <Button size="small" type="link" disabled>
                    Pick PO to seed lines
                  </Button>
                </Tooltip>
              )}
              {!isLocked && (
                <Button
                  size="small"
                  type="link"
                  onClick={addLine}
                  disabled={!header.poId && !isFromPo && !isEdit}
                >
                  Add non-PO line
                </Button>
              )}
            </div>
            <div className="grn-cards">
              {lines.length === 0 ? (
                <div className="grn-cards__empty">
                  {isLocked
                    ? 'No lines on this goods receipt.'
                    : (header.poId
                        ? 'No lines yet.'
                        : 'Pick a purchase order to seed lines, or add a non-PO line.')}
                </div>
              ) : (
                lines.map((row, idx) => renderLineCard(row, idx))
              )}
            </div>
          </div>

          {/* ── Reconciliation rail ──────────────────────────── */}
          <div className="grn-summary">
            <div className="grn-summary__hd">
              <span className="grn-summary__t">Reconciliation</span>
              <span className="grn-summary__sigma" aria-hidden>∑</span>
            </div>
            <div className="grn-summary__hero">
              <span className="grn-summary__hero-lab">Accepted</span>
              <span className="grn-summary__hero-val">{qty(summary.totalAcceptedQty)}</span>
              <span className="grn-summary__hero-sub">
                received {qty(summary.totalReceivedQty)} · rejected {qty(summary.totalRejectedQty)}
              </span>
            </div>
            <div className="grn-summary__bd">
            <div className="grn-summary__grid">
              <div className="grn-summary__label">Total Lines</div>
              <div className="grn-summary__value">{summary.totalLines}</div>

              <div className="grn-summary__label">Total Received Qty</div>
              <div className="grn-summary__value">
                {qty(summary.totalReceivedQty)}
              </div>

              <div className="grn-summary__label">Total Accepted Qty</div>
              <div className="grn-summary__value">
                {qty(summary.totalAcceptedQty)}
              </div>

              <div className="grn-summary__label">Total Rejected Qty</div>
              <div className="grn-summary__value">
                {qty(summary.totalRejectedQty)}
              </div>

              {showGlassCols && (
                <>
                  <div className="grn-summary__divider" />
                  <div className="grn-summary__label">Total Received SQMT</div>
                  <div className="grn-summary__value">
                    {qty(summary.totalReceivedSqmt)}
                  </div>
                  <div className="grn-summary__label">Total Received SQFT</div>
                  <div className="grn-summary__value">
                    {qty(summary.totalReceivedSqft)}
                  </div>
                </>
              )}

              {showLengthCols && (
                <>
                  <div className="grn-summary__divider" />
                  <div className="grn-summary__label">Total Received Length</div>
                  <div className="grn-summary__value">
                    {qty(summary.totalReceivedTotalLength)}
                  </div>
                </>
              )}

              <div className="grn-summary__divider" />

              <div className="grn-summary__label">Status</div>
              <div className="grn-summary__value">
                <span className="grn-status">
                  <span className="grn-sdot" style={{ background: statusDotColor(header.status || 'DRAFT') }} />
                  <span className="grn-sname">{statusLabel(header.status || 'DRAFT')}</span>
                </span>
              </div>
              <div className="grn-summary__label">Inspection</div>
              <div className="grn-summary__value">
                <span className="grn-status">
                  <span className="grn-sdot" style={{ background: statusDotColor(header.inspectionStatus || 'PENDING') }} />
                  <span className="grn-sname">{statusLabel(header.inspectionStatus || 'PENDING')}</span>
                </span>
              </div>
            </div>

            {/* Workflow chrono for context once posted. */}
            {(header.receivedAt || header.inspectedAt || header.acceptedAt)
              && (
              <div className="grn-summary__chrono">
                <Descriptions size="small" column={1} colon={false}>
                  {header.receivedAt && (() => {
                    const who = actorDisplayName(header.receivedByName, header.receivedBy);
                    return (
                      <Descriptions.Item label="Received">
                        {dayjs(header.receivedAt).format('DD-MMM-YY HH:mm')}
                        {who ? ` · ${who}` : ''}
                      </Descriptions.Item>
                    );
                  })()}
                  {header.inspectedAt && (() => {
                    const who = actorDisplayName(header.inspectedByName, header.inspectedBy);
                    return (
                      <Descriptions.Item label="Inspected">
                        {dayjs(header.inspectedAt).format('DD-MMM-YY HH:mm')}
                        {who ? ` · ${who}` : ''}
                      </Descriptions.Item>
                    );
                  })()}
                  {header.acceptedAt && (() => {
                    const who = actorDisplayName(header.acceptedByName, header.acceptedBy);
                    return (
                      <Descriptions.Item label="Accepted">
                        {dayjs(header.acceptedAt).format('DD-MMM-YY HH:mm')}
                        {who ? ` · ${who}` : ''}
                      </Descriptions.Item>
                    );
                  })()}
                </Descriptions>
              </div>
            )}
            </div>
            {!isLocked && (
              <div className="grn-summary__foot">
                <Button type="primary" block icon={<SaveOutlined />}
                  onClick={saveDraft} loading={saving} disabled={hasLineErrors}>
                  Save draft
                </Button>
                <span className="grn-summary__foot-note">
                  The backend re-checks every figure on save.
                </span>
              </div>
            )}
          </div>
        </div>
          </div>
        </div>
        </div>
      </Spin>

      {/* ── Cancel modal ──────────────────────────────────────── */}
      <Modal
        open={cancelOpen}
        title="Cancel goods receipt?"
        okText="Cancel GRN"
        okType="danger"
        cancelText="Back"
        confirmLoading={acting}
        onCancel={() => setCancelOpen(false)}
        onOk={confirmCancel}
      >
        <p className="text-sm text-slate-600 mb-2">
          Cancelling marks this GRN and all its lines as cancelled. The linked
          PO balance is restored automatically.
        </p>
        <Input.TextArea
          rows={3}
          maxLength={2000}
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          placeholder="Optional cancellation reason"
        />
      </Modal>

      {/* ── Inspect modal ─────────────────────────────────────── */}
      <Modal
        open={inspectOpen}
        title="Inspect goods receipt"
        okText="Save Inspection"
        cancelText="Back"
        width={900}
        confirmLoading={acting}
        onCancel={() => setInspectOpen(false)}
        onOk={submitInspect}
        destroyOnClose
      >
        <p className="text-sm text-slate-600 mb-2">
          Update accepted / rejected qty per line. A rejection reason is
          required when any quantity is rejected. The backend reconciles
          against received qty on save.
        </p>
        <Table
          className="grn-inspect-grid"
          size="small"
          rowKey="_clientRowId"
          dataSource={inspectLines}
          pagination={false}
          scroll={{ y: 360 }}
          columns={[
            {
              title: 'Item', dataIndex: 'item_name',
              render: (v, row) => (
                <div className="grn-inspect-item">
                  <div className="grn-inspect-item__name">{v || '—'}</div>
                  <div className="grn-inspect-item__type">
                    {row.item_type} · {row.uom}
                  </div>
                </div>
              )
            },
            {
              title: 'Received', dataIndex: 'received_qty', width: 90,
              render: (v) => qty(v)
            },
            {
              title: 'Accepted', dataIndex: 'accepted_qty', width: 110,
              render: (v, row) => {
                const a = Number(row.accepted_qty) || 0;
                const j = Number(row.rejected_qty) || 0;
                const r = Number(row.received_qty) || 0;
                const err = a + j - r > 1e-6;
                return (
                  <InputNumber
                    size="small"
                    min={0}
                    step={1}
                    controls={false}
                    status={err ? 'error' : undefined}
                    value={v ?? 0}
                    style={{ width: '100%' }}
                    onChange={(n) => patchInspectLine(row._clientRowId,
                      { accepted_qty: n == null ? 0 : Number(n) })}
                  />
                );
              }
            },
            {
              title: 'Rejected', dataIndex: 'rejected_qty', width: 110,
              render: (v, row) => {
                const a = Number(row.accepted_qty) || 0;
                const j = Number(row.rejected_qty) || 0;
                const r = Number(row.received_qty) || 0;
                const err = a + j - r > 1e-6;
                return (
                  <InputNumber
                    size="small"
                    min={0}
                    step={1}
                    controls={false}
                    status={err ? 'error' : undefined}
                    value={v ?? 0}
                    style={{ width: '100%' }}
                    onChange={(n) => patchInspectLine(row._clientRowId,
                      { rejected_qty: n == null ? 0 : Number(n) })}
                  />
                );
              }
            },
            {
              title: 'Reason', dataIndex: 'rejection_reason',
              render: (v, row) => {
                const required = (Number(row.rejected_qty) || 0) > 0;
                const missing = required && !String(v || '').trim();
                return (
                  <Input
                    size="small"
                    maxLength={500}
                    value={v || ''}
                    status={missing ? 'error' : undefined}
                    placeholder={required ? 'Required' : 'Optional'}
                    onChange={(e) => patchInspectLine(row._clientRowId,
                      { rejection_reason: e.target.value })}
                  />
                );
              }
            },
            {
              title: 'Status', dataIndex: 'inspection_status', width: 150,
              render: (v, row) => (
                <Select
                  size="small"
                  style={{ width: '100%' }}
                  value={v || 'PENDING'}
                  options={GRN_INSPECTION_STATUS_OPTIONS}
                  onChange={(val) => patchInspectLine(row._clientRowId,
                    { inspection_status: val })}
                />
              )
            }
          ]}
        />
        <div className="grn-inspect-notes">
          <label className="grn-inspect-notes__label">
            Inspection remarks
          </label>
          <Input.TextArea
            rows={2}
            maxLength={2000}
            value={inspectNotes}
            onChange={(e) => setInspectNotes(e.target.value)}
            placeholder="QC officer's overall notes"
          />
        </div>
      </Modal>
    </div>
  );
}
